/*********************************************************
 * Third Components
*********************************************************/
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { injectIntl, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect'

/*********************************************************
 * Components / Objects
*********************************************************/
import Map from '../../../../components/Map'
import PreferenceLoad from '../../main/components/PreferenceLoad'
import { TypeObjectMap, TypeLayer } from '../../../../components/Map/providers/enums'
import Preferences from 'Core/containers/Preferences'
import { NotificationManager } from "Components/Notifications";
import { HasPermission } from "Core/security"
import { MapUtilitiesTypes } from '../../main/components/MapUtilitiesMenu/enums';
import { geodesicMidpoint } from '../../../../components/Map/utils'
import { useDataGridThrottlerWithTrackedIds, useRealtimeTags } from 'Core/hooks';
/*********************************************************
 * Global Vars
*********************************************************/
let hiddenTagsPreferences = []

/*********************************************************
 * Redux Actions
*********************************************************/
import {
  addPointers,
  pointersCreated,
  setLatLng,
  setMapRestart,
  fleetVehicleGetStatuses,
  getLandmarksAndGeofences as getKmls,
  clearRedux,
  refreshDataVehicleList,
  getMarkerDetails,
  mapGetLandmarkTags,
  mapGetGeofenceTags,
  errorMapRoutesError,
  openModalMarkerRoute,
  onClickMarkerTrails,
  getPresignedUrlForKmzLayer,
  vehicleTrailsSearchClipRequests,
  setShowTagFIlterApplied
} from "Redux/actions";

import { setClipRequestData, resetClipRequestDrawer } from '../../../../components/Map/components/ClipRequestDrawerMap/redux/slice';
import { reset as resetVehicleTrailsSlice } from '../../main/components/VehicleTrails/redux/slice';

/*********************************************************
 * Area selectors / hooks
*********************************************************/
import { LoadLayersOnTheMap } from '../../main/components/LayerMap/utils';
import { getUnitsNaturalOrder, validateJsonObject } from '../../../../core/data/Helpers';

import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { driverSelectors } from 'Core/realtime/redux/driverSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);
const getVehicleTags = (state) => state.securityRedux?.user?.tagIds;
const getDriversDict = (state) => driverSelectors.selectEntities(state);

/********************************************************
 * Remove the true value, *** Remove this function
 * @param {string} field 
 ********************************************************/
const validateIntegrityField = (field) => {
  return (field + '').toString() != 'true' ? field : ''
}

/*********************************************************
 * Get information from pagination elements
*********************************************************/
const getData = createSelector(
  [getUnitsCatalog, getReadingsCatalog, getVehicleTags, getDriversDict],
  (units, readings, tagsPermissions, driversDict) => {
    const tagsEnableForUser = validateJsonObject(tagsPermissions) || [];
    let vehicleTags = tagsEnableForUser?.includes(-99) ? [] : tagsEnableForUser;
    let unitList = []
    try {
      units.forEach((unit) => {
        if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return
        const idUnit = unit.id
        // Phase 3.4 Optimization: O(1) Dictionary Lookup eliminating .find() bottleneck
        const driver = unit?.driverId ? (driversDict[unit.driverId] || null) : null;

        let lastReading = readings[idUnit]

        const lat = lastReading?.latitude || unit?.latitude
        const lng = lastReading?.longitude || unit?.longitud

        let _deviceTags = []

        if (unit && unit?.tags && Array.isArray(unit?.tags) && unit.tags?.length > 0) {
          unit.tags?.map(group => {
            let groupToPush = {}
            groupToPush.id = group?.id || -1
            _deviceTags.push(groupToPush)
          })
        }

        if (!unit?.tags || unit?.tags?.length == 0 || !Array.isArray(unit?.tags) || (_deviceTags?.length == 1 && _deviceTags[0]?.id == -1)) {
          //add the default group 
          unit = {
            ...unit,
            tags: [{
              color: "000000",
              hasChildren: false,
              id: -1,
              label: "Untagged",
              parentId: null,
              path: ""
            }]
          }
          _deviceTags?.length == 0 && _deviceTags.push({ id: -1 })
        }
        let hideUnitByTag = false
        if (hiddenTagsPreferences?.length > 0) {
          let countsItemsToHide = _deviceTags.length;
          _deviceTags.map((group) => {
            const removeGroup = hiddenTagsPreferences.find((hiddenG) => hiddenG == group.id)
            if (removeGroup >= 0) {
              countsItemsToHide--;
            }
          })
          if (countsItemsToHide == 0) hideUnitByTag = true
        }

        const driverName = driver ? validateIntegrityField(driver?.name) : validateIntegrityField(unit?.driverName);
        const label = unit?.label || lastReading?.deviceLabel
        //remove tags permisions with vehicleTags
        _deviceTags = vehicleTags?.length == 0 ? _deviceTags : _deviceTags.filter(tag => vehicleTags?.some(tagId => tagId === tag?.id));

        //filter unit for existing tag
        unit = {
          ...unit,
          tags: vehicleTags?.length == 0 ? unit.tags : unit.tags.filter(tag => vehicleTags?.some(tagId => tagId === tag?.id))
        }

        // if the unit no have tags or none is the group "Untagged", it is omitted
        if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions
        const haveLatLngValidate = Boolean((lat && !isNaN(lat) && parseFloat(lat) != 0) && (lng && !isNaN(lng) && parseFloat(lng) != 0));
        const item = {
          id: unit.id || lastReading?.deviceId,
          label: label,
          lat: lat ? parseFloat(lat) : 0,
          lng: lng ? parseFloat(lng) : 0,
          //haveLatLng: lastReading?.haveLatLng, // this vehicle no have lat and lng, cant show on map
          installed: unit?.installed || false,
          haveLatLng: unit?.installed && haveLatLngValidate,
          heading: lastReading?.heading || unit.heading || 0,
          eventName: lastReading?.eventName || unit.eventName || '',
          driverId: unit?.driverId,
          driverName: unit?.driverId > 0 ? driverName : '',
          unitGroups: unit?.unitGroups,
          deviceTags: _deviceTags,
          hasVideo: unit?.hasVideo || false,
          vehicleStatusId: unit?.vehicleStatusId,
          asset: unit?.asset,
          esn: unit?.esn,
          address: lastReading?.address,
          changeLabel: (unit.label != label),
          changeLatLng: lastReading?.changeLatLng,
          tags: unit?.tags,
          hideUnitByTag: hideUnitByTag,
          trail: lastReading?.trail || null,
          unitTime: lastReading?.unitTime || unit?.unitTime,
          rowId: lastReading?.rowId || 0,
          sdCard: unit?.sdCard || null,
          maintenanceWarningEventsCount: unit?.maintenanceWarningEventsCount || 0,
          isOffline: unit?.isOffline || false,
        }
        unitList.push(item);
      });
    } catch (e) {
      console.log('e', e)
      NotificationManager.error('Error', `Error /modules/realtimeMaps/ to create show vehicles - ${e}`, 3000, null, null, 'error');
    }

    return unitList
  }
)

/********************************************************
 * Global var to timer offline Devices
 ********************************************************/
let timerAnimation = null
let timerTrailCenterUnit = null

/*********************************************************
 * Class associated to the Main form at Realtime Maps
 ********************************************************/
const RealtimeMapsView = ({ mapRef }) => {

  const dispatch = useDispatch();

  const { locale, messages } = useIntl()

  const [prefHiddenGroups, setPrefHiddenGroups] = useState([])
  const [prefHiddenLandmarks, setPrefHiddenLandmarks] = useState([])
  const [prefHiddenGeofences, setPrefHiddenGeofences] = useState([])
  const [prefSettings, setPrefSettings] = useState()
  const [startingIcons, setStartingIcons] = useState(false)
  const [layersKmz, setLayersKmz] = useState([])
  const [initialLoad, setInitialLoad] = useState(true)

  const preferencesRef = useRef()

  /*********************************************************
   * Selectors
  *********************************************************/
  const centerUnitId = useSelector(state => state.realtimeMapRedux.centerUnitId);
  const currentZoom = useSelector(state => state.realtimeMapRedux.currentZoom);
  const noCenterPoints = useSelector(state => state.realtimeMapRedux.noCenterPoints);
  const followUnit = useSelector(state => state.realtimeMapRedux.followUnit);
  const pointers = useSelector(state => state.realtimeMapRedux.pointers);
  const kmlsToShowMap = useSelector(state => state.realtimeMapRedux.kmlsToShowMap);
  const pointOnMap = useSelector(state => state.realtimeMapRedux.pointOnMap);
  const markerRadius = useSelector(state => state.realtimeMapRedux.markerRadius);
  const directions = useSelector(state => state.realtimeMapRedux.directions);
  const loading = useSelector(state => state.realtimeMapRedux.loading);
  const generatePrint = useSelector(state => state.realtimeMapRedux.generatePrint);

  const jsonLandmark = useSelector(state => state.catalogsRealtimeRedux.landmarks);
  const jsonGeofence = useSelector(state => state.catalogsRealtimeRedux.geofences);

  const loadObjectLandmarkOnMap = useSelector(state => state.realtimeMapRedux.loadObjectLandmarkOnMap);
  const loadObjectGeofenceOnMap = useSelector(state => state.realtimeMapRedux.loadObjectGeofenceOnMap);
  const loadingLandmarkJson = useSelector(state => state.catalogsRealtimeRedux.loadingLandmarks);
  const loadingGeofenceJson = useSelector(state => state.catalogsRealtimeRedux.loadingGeofences);

  const vehicleTrails = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.vehicleTrails);
  const showVehicleTrails = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.showVehicleTrails);
  const isVehicleTrailsTrip = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.isVehicleTrailsTrip);
  const queryPagination = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.queryPagination);
  const loadVehicleTrails = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.loadVehicleTrails);
  const playback = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.playback);

  const createTypeSelect = useSelector(state => state.realtimeMapLandmarkModuleRedux.createTypeSelect);
  const createMaxPoints = useSelector(state => state.realtimeMapLandmarkModuleRedux.createMaxPoints);
  const flagLandmarkTagsVisible = useSelector(state => state.realtimeMapLandmarkModuleRedux.hiddenLandmarkGroups);
  const loadingLandmark = useSelector(state => state.realtimeMapLandmarkModuleRedux.loadingLandmark);
  const landmarks = useSelector(state => state.realtimeMapLandmarkModuleRedux.landmarks);

  const hiddenTagsIds = useSelector(state => state.realtimeMapUtilitiesVehiclesListRedux.hiddenTagsIds) || [];

  const sidebarShow = useSelector(state => state.commomRedux.sidebarShow);
  const loadTagsVehicleList = useSelector(state => state.realtimeMapUtilitiesVehiclesListRedux.loadTagsVehicleList);
  const flagGeofenceTagsVisible = useSelector(state => state.realtimeMapGeofenceModuleRedux.hiddenGeofenceGroups);
  const vehicleTrailsLayer = useSelector(state => state.realtimeMapLayerMapModuleRedux.vehicleTrailsLayer);
  const showVehicleTrailsLayer = useSelector(state => state.realtimeMapLayerMapModuleRedux.showVehicleTrailsLayer);
  const loadingRoute = useSelector(state => state.realtimeMapLayerMapModuleRedux.loadingRoute);
  const layerDrawRoute = useSelector(state => state.realtimeMapLayerMapModuleRedux.layerDrawRoute);
  const preferencesUser = useSelector(state => state.preferencesRedux.preferencesUser);
  const getMarkerDetailsId = useSelector(state => state.infoBubbleRedux.getMarkerDetailsId);
  const fleetGetVehicleStatesResult = useSelector(state => state.fleetMainRedux.fleetGetVehicleStatesResult);

  const kmlPushNotification = useSelector(state => state.catalogsRedux.kmlPushNotification);

  const showMapSettings = useSelector(state => state.realtimeMapUtilitiesRedux.showMapSettings);
  const showSpeedOverride = useSelector(state => state.realtimeMapSpeedOverrideCreateEditRedux.showSpeedOverride);
  const loadingStatingCatalog = useSelector(state => state.catalogsRealtimeRedux.loading);
  const loadingInitialCompleted = useSelector(state => state.catalogsRealtimeRedux.loadingInitialCompleted);
  const loadingUnitsL = useSelector(state => state.catalogsRedux.loadingUnitsList)
  const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits)
  const loadingUnitsList = loadingUnitsL || loadingUnits || loadingInitialCompleted || loadingStatingCatalog

  const showRoutesInfoMap = useSelector(state => state.realtimeMapsRoutesTasksRedux.showRoutesInfoMap);
  const showEditPanelRoute = useSelector(state => state.realtimeMapsRoutesModalCreateEditRoute.open);
  const landmarkTags = useSelector(state => state.realtimeMapLandmarkListModuleRedux.landmarkTags);
  const landmarkTagsLoading = useSelector(state => state.realtimeMapLandmarkListModuleRedux.landmarkTagsLoading);
  const geofenceTags = useSelector(state => state.realtimeMapNewGeofenceModuleRedux.geofenceTags);
  const geofenceTagsLoading = useSelector(state => state.realtimeMapNewGeofenceModuleRedux.geofenceTagsLoading);
  const user = useSelector(state => state.securityRedux.user);
  const { isKph } = user
  const geofenceModalEdit = useSelector(state => state.realtimeMapGeofenceCreateEditModuleRedux.open);
  const landmarkModalEdit = useSelector(state => state.realtimeMapLandmarkCreateEditModuleRedux.open);
  const etaModalEdit = useSelector(state => state.realtimeMapEtaLinkCreateEditRedux.open);
  const etaModalContacts = useSelector(state => state.etaContactsRedux.open);
  const etaModalDelete = useSelector(state => state.etaDeleteRedux.open);
  const openCreateRouteModal = useSelector(state => state.realtimeMapRoutesRedux.openCreateRouteModal);

  const currentModule = useSelector(state => state.realtimeMapUtilitiesRedux?.currentModule)
  const vicinityTool = currentModule === MapUtilitiesTypes.VicinityTool

  const openModalRoutesTemplates = useSelector(state => state.routesManagedCreateEditRouteModalMainRedux?.open);
  const openModalRoutesRealtimeMaps = useSelector(state => state.realtimeMapsRoutesModalCreateEditRoute.open);
  const loadingRoutesEdit = useSelector(state => state.realtimeMapsRoutesTasksRedux.loading);
  const presignedUrlsResponse = useSelector(state => state.filesRedux.presignedUrlsResponse);
  const unitVehicleTrails = useSelector(state => state.vehicleTrailsSliceRedux.unitVehicleTrails);
  const clipRequestsTrails = useSelector(state => state.vehicleTrailsSliceRedux.clipRequests);
  const loadingClipRequests = useSelector(state => state.vehicleTrailsSliceRedux.loadClipRequests);
  const clipRequestsInvalidated = useSelector(state => state.vehicleTrailsSliceRedux.clipRequestsInvalidated);
  const clipRequestsFilters = useSelector(state => state.vehicleTrailsSliceRedux.clipRequestsFilters);
  const clipRequestDrawerEvent = useSelector(state => state.clipRequestDrawerMapSlice.event);

  // Keep latest drawer event in a ref so map callbacks don't capture stale state.
  // The map provider may keep the original function reference when it binds listeners.
  const clipRequestDrawerEventRef = useRef(clipRequestDrawerEvent);

  const openModalRoutes = openModalRoutesTemplates || openModalRoutesRealtimeMaps || false

  const routeModalEdit = openCreateRouteModal?.open || false;

  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags', 'color', 'parentId'] });
  const landmarkTagsAssigned = landmarkTags && landmarkTags.length > 0 ? landmarkTags : tags

  //permission for animation trails
  const animationTrailPermission = !!(HasPermission(user, 'animationmap', [281]) && prefSettings?.animationmap);
  // User-controlled WebGL hardware acceleration toggle.  Defaults to ON to
  // preserve current behavior; users can disable it from Map Settings if
  // they experience graphics issues, which forces googleProvider to use
  // the legacy (non-WebGL, no mapId) renderer.
  // Note: preferences are persisted as 1/0 (see convertIntFromObjectToBoolean),
  // so we normalize to a strict boolean before passing it down.
  const _hwAccelRaw = prefSettings?.hardwareAcceleration;
  const hardwareAcceleration = (_hwAccelRaw === undefined || _hwAccelRaw === null) ? true : Boolean(_hwAccelRaw);
  /*********************************************************
   * Update elements to markers
  *********************************************************/
  const changeUpdate = useCallback((item, centerUnitId, followUnit, getMarkerDetailsId) => {

    if (!item.lat || !item.lng || !item.id) {
      return
    }

    mapRef.current?.updateObject({
      id: item.id,
      position: { lat: parseFloat(item.lat), lng: parseFloat(item.lng) },
      label: item.label,
      labelDriver: item?.driverName,
      heading: item.heading || 0,
      color: item.color,
      eventName: item.eventName,
      hasVideo: item?.hasVideo || false,
      trail: animationTrailPermission ? item?.trail || [] : [],
      unitTime: item.unitTime,
      rowId: item?.rowId || 0
    }, TypeObjectMap.Marker);

    //this validation preveng the movie modal when trail animation is active
    const addValidationTrail = animationTrailPermission && !item?.trail?.length

    if (addValidationTrail && getMarkerDetailsId && mapRef.current?.getOpenInfoWindow() && item.id == getMarkerDetailsId) {
      //Refresh data at the info window
      mapRef.current?.updateModalPosition({
        position: {
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lng)
        }
      })
    }

  })

  /*********************************************************
   * Clear the state realtime maps
  *********************************************************/
  useEffect(() => {

    //get the icon from vehicle status
    dispatch(fleetVehicleGetStatuses())

    dispatch(mapGetLandmarkTags())

    dispatch(mapGetGeofenceTags())

    dispatch(getKmls())

    //start the animation in map
    reproduceTrailEvent()


    const handleVisibilityChange = () => {
      if (document.hidden) {

        // lost the focus
        if (mapRef?.current) {

          //stop animation
          if (timerAnimation) {
            clearInterval(timerAnimation)
            timerAnimation = null
          }

          //stop the markers animation on map
          mapRef?.current?.focusStopTrailMarkersAnimations()
        }

      } else {
        // get the focus again...
        if (mapRef?.current) {
          //refresh the last animation into the queue
          mapRef?.current?.focusStartTrailMarkerAnimations();

          //start the trail events again
          reproduceTrailEvent()
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);


    return () => {
      mapRef?.current?.restartMap();
      if (timerAnimation) {
        clearInterval(timerAnimation);
        timerAnimation = null;
      }

      dispatch(setMapRestart())
      dispatch(clearRedux("TRAILS_REDUX"))
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  }, [])

  /*********************************************************
   * Update clip request ref
  *********************************************************/
  useEffect(() => {
    clipRequestDrawerEventRef.current = clipRequestDrawerEvent;
  }, [clipRequestDrawerEvent]);

  /*********************************************************
   * Refetch clip requests when a new one is created from the drawer
  *********************************************************/
  useEffect(() => {
    if (!clipRequestsInvalidated || !clipRequestsFilters?.dateRange) return;
    dispatch(vehicleTrailsSearchClipRequests({
      ...clipRequestsFilters,
      resetSearch: true,
      offset: 0,
    }));
  }, [clipRequestsInvalidated, dispatch]);

  /*********************************************************
   * Update kml from push notifications
  *********************************************************/
  useEffect(() => {
    if (kmlPushNotification > 0) {
      dispatch(getKmls())
    }
  }, [kmlPushNotification])

  useEffect(() => {
    if (centerUnitId > 0 && followUnit) {
      const mapObjectAnimated = mapRef.current.getObject(TypeObjectMap.Marker, centerUnitId);
      if (mapObjectAnimated) mapObjectAnimated.followMarker = true;
      //update the size on icon
      //mapRef?.current?.setSizeMarkerIcon(centerUnitId, TypeObjectMap.Marker);

    } else {
      //set the icon to the normal size 
      if (mapRef.current) {
        const mapObjectAnimated = mapRef?.current?.getObject(TypeObjectMap.Marker, centerUnitId);
        if (mapObjectAnimated) mapObjectAnimated.followMarker = false;
        //update the icons
        //mapRef?.current?.setSizeMarkerIcon(centerUnitId, TypeObjectMap.Marker);
      }
    }
  }, [centerUnitId, followUnit])

  const reproduceTrailEvent = () => {
    if (timerAnimation) {
      clearInterval(timerAnimation)
      timerAnimation = null
    }
    timerAnimation = setInterval(animationTrailEvents, 3000);
  }

  const animationTrailEvents = () => {
    mapRef?.current?.reproduceTrailEvents()
  }

  /*********************************************************
   * Load the preference mapSettings values prevenge reload
  *********************************************************/
  useEffect(() => {
    try {
      if (preferencesUser) {
        const mapSettings = preferencesUser.find(preference => preference.preferenceKey === "mapSettings");
        if (mapSettings && mapSettings?.preferenceValue && typeof mapSettings?.preferenceValue === "string") {
          if (JSON.stringify(prefSettings) != mapSettings.preferenceValue) {
            let preferences = JSON.parse(mapSettings.preferenceValue);
            if (typeof preferences === "string") preferences = JSON.parse(preferences);

            setPrefSettings({ ...preferences });
          }
        }

        const layers = preferencesUser.find(preference => preference.preferenceKey === "layers");
        if (layers && layers?.preferenceValue && typeof layers?.preferenceValue === "string") {
          let preferencesLayers = JSON.parse(layers?.preferenceValue);
          let key = user?.companyId
          if (user?.isSubUser) {
            key = user?.subUserId
          }
          setLayersKmz(preferencesLayers[key]);
        }


        const realtimeSettings = preferencesUser.find(preference => preference.preferenceKey === "realtimeMaps");
        if (realtimeSettings && realtimeSettings?.preferenceValue) {
          if (hiddenTagsPreferences != realtimeSettings?.hiddenTagsOnPreference)
            hiddenTagsPreferences = realtimeSettings?.hiddenTagsOnPreference || [];
        }


      }
    } catch (e) {
      NotificationManager.error('Error', `Error /modules/realtimeMaps/ preferences map settings - ${e}`, 3000, null, null, 'error');
    }
  }, [preferencesUser])

  useEffect(() => {
    if (mapRef?.current) {
      if (openModalRoutes) {
        mapRef?.current?.addMapClickRouteListener()
      } else {
        mapRef?.current?.removeMapClickRouteListener()
      }
    }
  }, [openModalRoutes])

  //change comments to english please
  useEffect(() => {
    if (presignedUrlsResponse && Array.isArray(presignedUrlsResponse) && presignedUrlsResponse.length > 0) {
      // Load new layers on the map, using the enriched array with presigned URLs
      LoadLayersOnTheMap(
        presignedUrlsResponse,              // The array with each item and its presignedUrl
        mapRef                              // Reference to the current map item
      );
    }
  }, [presignedUrlsResponse]);

  useEffect(() => {
    if (layersKmz?.length > 0) {
      //get presinged urls and load the layers on the map
      dispatch(getPresignedUrlForKmzLayer(layersKmz));
    }
  }, [layersKmz])

  /*********************************************************
   * Dispatch to clip request data from polyline clicked
  *********************************************************/
  const polyLineClicked = useCallback((data) => {
    const currentEvent = clipRequestDrawerEventRef.current;
    const event = { id: data?.rowId, unitId: data?.unitId };

    //get the midpoint from the polyline to set in the clip request drawer
    const { lat, lng } = geodesicMidpoint(data?.arrayPositions[0], data?.arrayPositions[1])

    const roundCoord = (value) => {
      const numberValue = typeof value === 'string' ? Number(value) : value;
      if (!Number.isFinite(numberValue)) return 0;
      return numberValue.toFixed(5);
    };
    const isSameEvent =
      String(currentEvent?.id ?? '') === String(event.id ?? '') &&
      String(currentEvent?.unitId ?? '') === String(event.unitId ?? '');

    if (isSameEvent) {
      dispatch(resetClipRequestDrawer());
      return;
    }

    dispatch(
      setClipRequestData({
        event: { ...event, unitTime: data?.unitTime, lat: roundCoord(lat), lng: roundCoord(lng) },
      })
    );
  }, [dispatch]);

  /*********************************************************
   * evaluate equals values into realtime maps preferences
  *********************************************************/
  const equals = (a, b) => a?.length === b?.length && a?.every((v, i) => v === b[i]);

  /*********************************************************
   * Return the position from MapClick event in map
  *********************************************************/
  const onMapClick = (latLng) => {
    dispatch(setLatLng(latLng))
  }

  /*********************************************************
   * Event for marker click on vehicle Trails Module
   * @param {*} itemId 
   *********************************************************/
  const onClickMarkerEvent = (itemId) => {
    dispatch(onClickMarkerTrails(itemId))
  }

  const onRouteMarkerClick = (event) => {
    dispatch(openModalMarkerRoute({ open: true, data: event?.mapObject }))
  }

  const onRouteMapClick = () => {
    //al markers to routes display the normal position
    mapRef.current.showNormalMarkers()
    dispatch(openModalMarkerRoute({ open: false, data: null }))
  }

  /*********************************************************
   * update the icon in realtime maps
  *********************************************************/
  const updateIconsInMap = (itemsChanged) => {

    if (itemsChanged.changes && itemsChanged.changes.length <= 0) return

    const itemsToUpdate = itemsChanged.changes?.filter((itemChange) => itemChange.atribute == 'vehicleStatusId') || []

    if (itemsToUpdate.length <= 0) return //no find elements to update        

    itemsToUpdate.forEach((item) => {
      if (item.newValue != 0 && fleetGetVehicleStatesResult?.data && fleetGetVehicleStatesResult?.data?.length > 0) {
        const vehicleIcon = fleetGetVehicleStatesResult.data.find(vehicle => parseInt(vehicle.id) == parseInt(item.newValue))
        if (vehicleIcon && vehicleIcon.icon) {
          mapRef?.current?.updateMarkerIconMap(item.id, vehicleIcon.icon);
        } else {
          mapRef?.current?.updateMarkerIconMap(item.id, '');
        }
      } else if (item.newValue == 0) {
        mapRef?.current?.updateMarkerIconMap(item.id, '');
      }
    })
  }

  const updateInfoModalInformation = (itemsChanged) => {

    const itemIdIsComing = itemsChanged.data.find((item) => item.id == getMarkerDetailsId);

    if (getMarkerDetailsId && mapRef.current?.getOpenInfoWindow() && itemIdIsComing) {
      setTimeout((id) => {
        dispatch(getMarkerDetails({ type: "Vehicle", id: id, unitId: null }));
      }, 1000, itemIdIsComing.id)
    }
  }

  /*********************************************************
   * use compute data to sync values showing in table
  *********************************************************/
  const handleMapUpdates = useCallback((itemsChanged) => {
    if (!itemsChanged || !itemsChanged?.data || itemsChanged && itemsChanged.data?.length <= 0) return;
    const procecedItems = [...itemsChanged.data];
    try {
      //first update the icons positions
      updateIconsInMap(itemsChanged);

      itemsChanged.data.forEach((itemChange) => {
        changeUpdate(itemChange, centerUnitId, followUnit, getMarkerDetailsId)

        const index = procecedItems.indexOf(itemChange);
        if (index > -1) { // only splice array when item is found
          procecedItems.splice(index, 1); // 2nd parameter means remove one item only
        }
      })

      itemsChanged?.changes.forEach((change) => {
        if (change?.atribute === "isOffline") {
          mapRef.current?.updateMarkerOfflineDevice(change.id, change?.newValue)
        }
      })

      //update vehicle list for changes in 'label', 'driverId', 'driverName', 'haveLatLng'
      //updateVehicleListForCatalog(itemsChanged);

      //Update info modal information
      updateInfoModalInformation(itemsChanged)
    } catch (e) {
      console.log('Error', `Error /modules/realtimeMaps/useComputeData update marker`, e);
    }
  }, [updateIconsInMap, changeUpdate, centerUnitId, followUnit, getMarkerDetailsId]);

  const computedData = useSelector(state => getData(state) || []);
  const propertiesToValidate = useMemo(() => ["label", "lat", "lng", "eventName", "vehicleStatusId", "changeLatLng", "haveLatLng", "driverId", "driverName", "hideUnitByTag", "trail", "sdCard", "tags", "maintenanceWarningEventsCount", "isOffline"], []);

  // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
  const [data, changeIdsCallback] = useDataGridThrottlerWithTrackedIds(
    computedData,
    1000,
    handleMapUpdates,
    "id",
    propertiesToValidate
  );

  /*********************************************************
   * Change the icon from vehicle trails information
  *********************************************************/
  useEffect(() => {
    if (!startingIcons && data && data.length > 0 &&
      fleetGetVehicleStatesResult && fleetGetVehicleStatesResult.data && fleetGetVehicleStatesResult.data.length > 0) {
      setStartingIcons(true)
      setTimeout(() => {
        data.forEach(item => {
          const vehicleIcon = fleetGetVehicleStatesResult.data.find(vehicle => item.vehicleStatusId == vehicle.id)
          if (vehicleIcon && vehicleIcon.icon) {
            mapRef.current?.updateMarkerIconMap(item.id, vehicleIcon.icon);
          }
        })
      }, 500)
    }


  }, [fleetGetVehicleStatesResult, data]);

  return (
    <Preferences
      ref={preferencesRef}
      preferencesKey="realtimeMaps"
      force={true}
      onInitialPreferences={(preferences) => {
        /* Hidden groups preferences */
        hiddenTagsPreferences = preferences?.hiddenTagsOnPreference || [];
        if (prefHiddenGroups.length > 0 && !equals(prefHiddenGroups, preferences?.hiddenTagsOnPreference)) {
          setPrefHiddenGroups(preferences?.hiddenTagsOnPreference || []);
          setStartingIcons(false);
        } else {
          if (prefHiddenGroups.length == 0 && preferences?.hiddenTagsOnPreference?.length > 0) {
            setPrefHiddenGroups(preferences?.hiddenTagsOnPreference || []);
            setStartingIcons(false);
          }
        }
        /* hidden landmark preferences */
        if (prefHiddenLandmarks.length > 0 && !equals(prefHiddenLandmarks, preferences?.hiddenLandmarkGroups && preferences?.hiddenLandmarkGroups.length > 0)) {
          setPrefHiddenLandmarks([...preferences?.hiddenLandmarkGroups] || []);
        } else {
          if (prefHiddenLandmarks.length == 0 && preferences?.hiddenLandmarkGroups?.length > 0) {
            setPrefHiddenLandmarks([...preferences?.hiddenLandmarkGroups] || []);
          }
        }
        if (prefHiddenGeofences.length > 0 && !equals(prefHiddenGeofences, preferences?.hiddenGeofenceGroups && preferences?.hiddenGeofenceGroups.length > 0)) {
          setPrefHiddenGeofences([...preferences?.hiddenGeofenceGroups] || []);
        } else {
          if (prefHiddenGeofences.length == 0 && preferences?.hiddenGeofenceGroups?.length > 0) {
            setPrefHiddenGeofences([...preferences?.hiddenGeofenceGroups] || []);
          }
        }
      }}
    >
      <Map ref={mapRef}
        showKmlButtonMap
        showButtonZoomVehicles
        userMapPreferences={prefSettings}
        units={data.filter((item) => item.haveLatLng && !item?.hideUnitByTag)}
        landmarks={landmarks}
        centerUnitId={centerUnitId}
        currentZoom={currentZoom}
        getMarkerDetailsId={getMarkerDetailsId}
        //add this props to follow modal in trails
        followUnit={followUnit}
        pointers={pointers}
        noCenterPoints={noCenterPoints}
        hiddenTagsIds={hiddenTagsIds} // new hidden tags values for map
        hiddenTagsPreferences={prefHiddenGroups}
        loadingUnits={loadingUnitsList}
        loadTagsVehicleList={loadTagsVehicleList}
        generatePrint={generatePrint}
        isKph={isKph}
        locale={locale}
        /* Show the progress bar in map */
        loading={loading
          || loadingUnitsList
          || loadVehicleTrails
          || loadingLandmark
          || loadingRoute
          || loadingLandmarkJson
          || loadingGeofenceJson
          || landmarkTagsLoading
          || geofenceTagsLoading
          || loadingRoutesEdit}
        /* Data to kml files to show in map */
        kmlsToShowMap={kmlsToShowMap}
        messages={messages}
        /* Vehicle trails data from create markers in map */
        vehicleTrails={vehicleTrails}
        /* Show or hide vehicle trails */
        showVehicleTrails={showVehicleTrails}
        isVehicleTrailsTrip={isVehicleTrailsTrip}
        /* Param to resize map */
        showMapSettings={showMapSettings}
        /* Show or hide sidebar (left element in view) to minimize map */
        sidebarShow={sidebarShow}
        /* Type select map to create polygon or circle */
        createTypeSelect={createTypeSelect}
        /* Maximum points to polygon create */
        createMaxPoints={createMaxPoints}
        /* Create the marker point into the map */
        pointOnMap={pointOnMap}
        /* pass the create pointer function */
        addPointers={addPointers}
        /* event to created poinst flag */
        pointersCreated={pointersCreated}
        /* add the marker radius to marker edition circle */
        markerRadius={markerRadius}
        /* hide landmarks groups in map / hide kmls */
        flagLandmarkTagsVisible={flagLandmarkTagsVisible}
        /* hide geofence groups in map / hide kmls */
        flagGeofenceTagsVisible={flagGeofenceTagsVisible}
        /* hide landmarks groups in map / hide kmls by preferences */
        prefHiddenLandmarks={prefHiddenLandmarks}
        prefHiddenGeofences={prefHiddenGeofences}
        /* Vehicle trails data from create markers in map *Layer* */
        vehicleTrailsLayer={vehicleTrailsLayer}
        layerDrawRoute={layerDrawRoute}
        /* Show or hide vehicle trails *Layer* */
        showVehicleTrailsLayer={showVehicleTrailsLayer}
        /* When the user Click on the map and TypeObjectMap is TypeObjectMap.MapClick */
        onMapClick={onMapClick}
        onClickMarkerEvent={onClickMarkerEvent}
        /* Draw the points [] to directions into the realtime maps */
        directions={directions}
        /* props to map Settinsgs */
        queryPagination={queryPagination}
        /* Playback route in vehicle trails */
        playback={playback}
        /* Landmarks groups to get color and permisions.  */
        landmarkTags={landmarkTagsAssigned}
        geofenceTags={geofenceTags}
        landmarkTagsLoading={landmarkTagsLoading}
        geofenceTagsLoading={geofenceTagsLoading}
        /* Landmarks data to load map */
        jsonLandmark={jsonLandmark}
        /* Geofence data to load map */
        jsonGeofence={jsonGeofence}
        /* Flag to create instance landmarks and geofences into the map by zoom event */
        loadObjectLandmarkOnMap={loadObjectLandmarkOnMap}
        loadObjectGeofenceOnMap={loadObjectGeofenceOnMap}
        /* show the control for colors at speed Override on map */
        showSpeedOverride={showSpeedOverride}
        /* show the control for colors at route points on map */
        showRoutesInfoMap={showRoutesInfoMap}
        tabEditMode={routeModalEdit || landmarkModalEdit || geofenceModalEdit || etaModalEdit || etaModalDelete || etaModalContacts}
        /* error in draw route */
        errorMapRoutesError={errorMapRoutesError}
        onBoundsChanged={(units) => {
          changeIdsCallback(units, "id")
        }}
        weatherPermision={HasPermission(user, "weather")}
        isEditRoutesModule={openModalRoutes}
        onRouteMarkerClick={onRouteMarkerClick}
        onRouteMapClick={onRouteMapClick}
        classNameToMap={showEditPanelRoute && sidebarShow ? 'realtimeMaps-route-class-element-map-with-sidebar' :
          showEditPanelRoute && !sidebarShow ? 'realtimeMaps-route-class-element-map-without-sidebar' : ''}
        vicinityTool={vicinityTool}
        animationTrailPermission={animationTrailPermission}
        hardwareAcceleration={hardwareAcceleration}
        polyLineClicked={polyLineClicked}
        unitVehicleTrails={unitVehicleTrails}
        clipRequestsTrails={clipRequestsTrails}
        loadingClipRequests={loadingClipRequests}
      >

      </Map>
      <PreferenceLoad mapRef={mapRef} />
    </Preferences>
  );
}

export default injectIntl(RealtimeMapsView);
